<template>
  <div v-if="author && author.name && author.job && author.vip">
    <Sections
      :key="`personal-information-${author.id}`"
      class="bg-orange-very-light md:mt-0"
      grid="I"
      padding-y="pt-0 pb-14 md:py-14"
    >
      <template #content-left>
        <div class="gap-4 my-10 md:my-0">
          <div v-if="author?.image?.url" class="flex justify-center flex-cols">
            <NuxtImg
              :key="author.image.url"
              loading="lazy"
              class="w-40 h-40 flex-none rounded-full object-cover"
              :title="author.name"
              :src="author.image.url"
              :alt="author.image.alternativeText ?? 'image autor'"
            />
          </div>
          <div v-else class="flex justify-center flex-cols">
            <icon
              name="pt-user"
              class="pt-6 pl-8 pr-8 mr-4 border-2 rounded-full lg:w-40 lg:h-40 bg-white-dark text-gray-light text-8xl border-gray-light"
            />
          </div>
          <div v-if="author?.social_networks && Object.keys(author?.social_networks)?.length">
            <ul class="flex items-center justify-center">
              <li
                v-for="social in socialNetworks(author.social_networks)"
                :key="social.key"
                class="m-2"
              >
                <a rel="nofollow" :href="social.value" target="_blank">
                  <icon class="text-3xl sm:text-4xl" :name="social.key" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </template>
      <template #content-right>
        <h1
          v-if="author.name"
          class="text-2xl font-semibold normal-case text-orange-light"
          v-text="author.name"
        />
        <span v-if="author.job" class="block my-3 font-sans text-lg" v-text="author.job" />
        <Markdown v-if="author.short_cv" :content="author.short_cv" class="my-3 text-lg" />
      </template>
    </Sections>
    <div v-if="loading" class="w-full flex justify-center">
      <icon name="spinner" class="text-5xl text-blue animate-spin mr-2 p-16" />
    </div>
    <Sections
      v-if="blogsByAuthor?.length && !loading"
      :key="`publications-${author.id}`"
      class="md:mt-0 bg-blue-dark"
      grid="D"
      padding-y="pt-0 pb-14 md:py-14"
    >
      <template #content-right>
        <span class="block text-lg font-bold font-ubuntu tracking-normal text-white">
          {{ $t('author.communityBlog') }}:
        </span>
        <div class="flex flex-col">
          <div
            v-for="blog in blogsByAuthor"
            :key="blog.slug"
            class="flex flex-col space-y-5 py-6 border-b-2 border-gray-very-light hover:border-blue"
          >
            <span>
              <NuxtLink
                class="text-2xl font-ubuntu font-normal text-white"
                :to="`${COMMUNITY_BASE_URL}/${blog.slug}`"
              >
                {{ blog.name }}
              </NuxtLink>
            </span>
          </div>
        </div>
      </template>
    </Sections>
    <Sections
      v-if="setterPublications?.length && !loading"
      :key="`publications-${author.id}`"
      class="bg-white md:mt-0"
      grid="D"
      padding-y="pt-0 pb-14 md:py-14"
    >
      <template #content-right>
        <span class="block text-lg font-bold font-ubuntu tracking-normal">
          {{ $t('author.autorArticlesAndPublications') }}:
        </span>
        <CardsResults
          v-for="item in setterPublications"
          :key="`${item.slug}-1`"
          :slug="item.slug"
          :title="item.title"
          :date="dateToStringWithNameMonth(item.publishAt)"
          :category="item.category"
          color="gray"
        />
      </template>
    </Sections>
    <ClientOnly>
      <Sections
        v-if="authorEvents?.length && !loading"
        class="bg-gray-very-light md:mt-0"
        grid="E"
        padding-y="pt-1 pb-14 md:py-14"
      >
        <template #header>
          <span
            class="container block my-5 md:mt-0 text-lg font-bold font-ubuntu tracking-normal"
            v-text="`${$t('author.autorEvents')}:`"
          />
        </template>
        <template #content>
          <div class="swiper grid grid-flow-col-dense lg:grid-flow-row lg:grid-cols-12 gap-6">
            <CardsRelatedCourse
              v-for="event in authorEvents"
              :key="event.id"
              :course="event"
              :base-url="COURSE_BASE_URL"
            />
          </div>
        </template>
      </Sections>
      <BannersMultimediaSubscribe
        v-if="!$store.getters['auth/getByField']('loggedIn')"
        :description="$t('author.recognition')"
        :title="$t('author.subscribeToBenefit')"
      />
    </ClientOnly>
  </div>
</template>

<script setup>
import { dateToStringWithNameMonth } from '@/utils/helpers/date';
import {
  COURSE_BASE_URL,
  RETRIEVE_INDEXES,
  COMMUNITY_BASE_URL,
  STRUCTURED_DATA_TYPE
} from '~/utils/enums';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { createHeadSeo } = useHeadSeo();
const { instantMeili } = useInstantSearch();

const state = reactive({
  authorEvents: [],
  loading: false
});
const { authorEvents, loading } = toRefs(state);

onMounted(async () => {
  await store.dispatch('strapi/getArticlesByAuthor', { slug: route.params.slug });
  await store.dispatch('strapi/getArticlesByCoordinator', { slug: route.params.slug });
  await store.dispatch('strapi/getBlogsByAuthor', { slug: route.params.slug });
  getAuthorEvents();
});

const { data } = await useAsyncData('authorDetail', async () => {
  const author = await store.dispatch('strapi/getAuthorBySlug', {
    slug: route.params.slug
  });

  return {
    author
  };
});
if (
  data?.value &&
  !data?.value?.author[0]?.vip &&
  (!data?.value?.author[0]?.job || !data?.value?.author[0]?.name)
)
  throw createError({
    statusCode: 404,
    fatal: true
  });

const author = computed(() => (data?.value ? data?.value?.author[0] : null));
const authorPublications = computed(() => store.getters['strapi/getByField']('authorPublications'));
const coordinatorPublications = computed(() =>
  store.getters['strapi/getByField']('coordinatorPublications')
);

const blogsByAuthor = computed(() => store.getters['strapi/getByField']('blogsByAuthor'));

const setterPublications = computed(() => [
  ...authorPublications.value,
  ...coordinatorPublications.value
]);

const getAuthorEvents = () => {
  instantMeili.value.meiliSearchInstance
    .index('events')
    .search('', {
      filter: `speakers=${route.params.slug}`,
      attributesToRetrieve: RETRIEVE_INDEXES.events || ['*']
    })
    .then(({ hits }) => {
      authorEvents.value = hits;
    })
    .catch(err => err);
};

const socialNetworks = networks => {
  const result = [];
  Object.entries(networks).forEach(([key, value]) => {
    if (value) {
      result.push({
        key,
        value
      });
    }
  });
  return result;
};

watch(
  author,
  val => {
    if (val) {
      const { name, short_cv: shortCv, image } = val;
      createHeadSeo(
        t('metaTags.metaTitle.author', { name, appName: process.env.APP_NAME }),
        shortCv,
        image ? image.url : '',
        val,
        STRUCTURED_DATA_TYPE.AUTHOR
      );
    }
  },
  { immediate: true, deep: true }
);
</script>
